/**
 * Anthropic Claude provider adapter.
 * All requests proxy through the sidecar (desktop) or Vercel edge function (web).
 * API keys are never exposed client-side.
 */

import type {
  AIProviderAdapter,
  AICompletionRequest,
  AICompletionResponse,
} from './base-provider';
import { getApiBase } from '@/app/api-router';

const COMPLETION_TIMEOUT_MS = 30_000;
const HEALTH_CHECK_TIMEOUT_MS = 5_000;
const MAX_CONSECUTIVE_FAILURES = 3;

const AVAILABLE_MODELS = [
  'claude-sonnet-5',
  'claude-haiku-4-5-20251001',
  'claude-opus-5',
];

export class AnthropicProvider implements AIProviderAdapter {
  readonly id = 'anthropic';
  readonly name = 'Anthropic Claude';
  readonly supportsStructuredOutput = true;
  readonly supportsGeneration = true;

  private consecutiveFailures = 0;

  async isAvailable(): Promise<boolean> {
    // After N consecutive failures, report unavailable until TTL expires
    if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      return false;
    }

    // Health check: POST an invalid body to the proxy endpoint.
    // The real API proxy validates and returns 400 with Content-Type: application/json.
    // SPA fallback (Vite dev server) returns 200 with Content-Type: text/html.
    // Checking content-type distinguishes the two reliably.
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
      const res = await fetch(`${getApiBase()}/api/ai/anthropic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        signal: controller.signal,
      });
      clearTimeout(timer);
      const ct = res.headers.get('content-type') ?? '';
      return ct.includes('application/json');
    } catch {
      return false;
    }
  }

  async listModels(): Promise<string[]> {
    return [...AVAILABLE_MODELS];
  }

  async complete(request: AICompletionRequest): Promise<AICompletionResponse> {
    const model = request.model ?? AVAILABLE_MODELS[1]; // Default to haiku for cost
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), COMPLETION_TIMEOUT_MS);
    const start = performance.now();

    // No temperature: sampling parameters are rejected (400) on the
    // Claude 5 family (Sonnet 5 / Opus 5); the model default applies.
    const reqBody: Record<string, unknown> = {
      model,
      max_tokens: request.maxTokens ?? 512,
      system: request.systemPrompt,
      messages: [{ role: 'user', content: request.prompt }],
    };

    const outputConfig: Record<string, unknown> = {};
    if (request.effort) {
      outputConfig.effort = request.effort;
    }
    if (request.outputSchema) {
      // Wire shape is { type, schema } — a json_schema key is rejected.
      outputConfig.format = {
        type: 'json_schema',
        schema: request.outputSchema,
      };
    }
    if (Object.keys(outputConfig).length > 0) {
      reqBody.output_config = outputConfig;
    }

    const body = JSON.stringify(reqBody);

    try {
      // Route through sidecar (desktop) or Vercel edge function (web)
      const res = await fetch(`${getApiBase()}/api/ai/anthropic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal,
      });

      clearTimeout(timer);

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`Anthropic returned ${res.status}: ${errText}`);
      }

      const data = (await res.json()) as {
        content: { type: string; text: string }[];
        usage?: { input_tokens: number; output_tokens: number };
      };

      const text = data.content
        .filter(c => c.type === 'text')
        .map(c => c.text)
        .join('');

      // Reset failure counter on success
      this.consecutiveFailures = 0;

      const trimmedText = text.trim();
      const result: AICompletionResponse = {
        text: trimmedText,
        model,
        provider: this.id,
        tokensUsed: data.usage
          ? data.usage.input_tokens + data.usage.output_tokens
          : undefined,
        latencyMs: Math.round(performance.now() - start),
      };

      // When structured output was requested, the API guarantees valid JSON
      if (request.outputSchema && trimmedText) {
        try {
          result.structuredOutput = JSON.parse(trimmedText);
        } catch {
          // Fall through to text-only response
        }
      }

      return result;
    } catch (err) {
      clearTimeout(timer);
      this.consecutiveFailures++;
      throw err;
    }
  }

  async validate(): Promise<{ valid: boolean; error?: string }> {
    try {
      const result = await this.complete({
        prompt: 'ping',
        maxTokens: 5,
      });
      return result.text.length > 0
        ? { valid: true }
        : { valid: false, error: 'Empty response from Anthropic' };
    } catch (err) {
      return {
        valid: false,
        error: err instanceof Error ? err.message : 'Validation failed',
      };
    }
  }
}
